// src/views/auth/SessionExpiredModal.jsx
import { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

const COUNTDOWN = 5;

export default function SessionExpiredModal() {
    const [visible, setVisible] = useState(false);
    const [count, setCount] = useState(COUNTDOWN);
    const { signout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const handler = () => { setVisible(true); setCount(COUNTDOWN); };
        window.addEventListener("session:expired", handler);
        return () => window.removeEventListener("session:expired", handler);
    }, []);

    useEffect(() => {
        if (!visible) return;
        if (count <= 0) {
            signout();
            navigate("/auth/sign-in", { replace: true });
            setVisible(false);
            return;
        }
        const t = setTimeout(() => setCount((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [visible, count, signout, navigate]);

    if (!visible) return null;

    const circumference = 2 * Math.PI * 24;

    const handleLoginNow = () => {
        signout();
        navigate("/auth/sign-in", { replace: true });
        setVisible(false);
    };

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] animate-[fadeIn_0.2s_ease]" />

            {/* Modal Card */}
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="se-title"
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999]
                   bg-white rounded-2xl shadow-2xl
                   w-[min(88vw,360px)] px-8 pt-9 pb-7
                   text-center
                   animate-[slideUp_0.3s_cubic-bezier(0.34,1.56,0.64,1)]"
            >
                {/* Icon circle */}
                <div className="inline-flex items-center justify-center w-17 h-17 rounded-full bg-red-50 mb-4">
                    <svg
                        width="32" height="32" viewBox="0 0 24 24" fill="none"
                        stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>

                {/* Title */}
                <h2 id="se-title" className="text-xl font-bold text-gray-800 mb-2">
                    Session Expired
                </h2>

                {/* Body */}
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                    Your session has expired for security reasons.
                    <br />
                    Please log in again to continue.
                </p>

                {/* Countdown ring */}
                <div className="flex flex-col items-center gap-1.5 mb-6">
                    <svg width="64" height="64" viewBox="0 0 56 56">
                        <circle cx="28" cy="28" r="24" fill="none" stroke="#F3F4F6" strokeWidth="4" />
                        <circle
                            cx="28" cy="28" r="24"
                            fill="none"
                            stroke="#E53E3E"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={circumference * (1 - count / COUNTDOWN)}
                            transform="rotate(-90 28 28)"
                            style={{ transition: "stroke-dashoffset 1s linear" }}
                        />
                        <text
                            x="28" y="34"
                            textAnchor="middle"
                            style={{ fontSize: 16, fontWeight: 700, fill: "#E53E3E" }}
                        >
                            {count}
                        </text>
                    </svg>
                    <p className="text-xs text-gray-400">Redirecting in {count}s…</p>
                </div>

                {/* Button */}
                <button
                    onClick={handleLoginNow}
                    className="w-full py-3 bg-red-500 hover:bg-red-600 active:scale-[0.98]
                     text-white text-sm font-semibold rounded-lg
                     transition-all duration-150 cursor-pointer"
                >
                    Log In Now
                </button>
            </div>

            <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, -44%) scale(0.96); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1);    }
        }
      `}</style>
        </>
    );
}
